/**
 * Draft-night clocks. No rule governs any of them, so there are no citations in this file.
 *
 * Every clock is advisory except `bidHasSold` — an expired pick clock turns red and nothing else
 * happens. A standing farm bid has to sell, or the auction never terminates.
 */

import type { ClockBand, DraftClocks } from '../store/types.js';

/** A starting point, not law. */
export const SUGGESTED_CLOCKS: DraftClocks = {
  pickBands: [
    { fromRound: 1, toRound: 4, seconds: 120 },
    { fromRound: 5, toRound: 16, seconds: 60 },
  ],
  intermissionAfterRound: 8,
  intermissionMinutes: 10,
  nominateSeconds: 60,
  bidSellsAfterSeconds: 20,
};

/** How long this round's picks get. Null — no clock set — is not zero. */
export function pickSeconds(clocks: DraftClocks | null | undefined, round: number): number | null {
  const band = (clocks?.pickBands ?? [])
    .find((b) => round >= b.fromRound && round <= b.toRound && b.seconds > 0);
  return band ? band.seconds : null;
}

/** What is wrong with a set of bands. Overlaps are errors; gaps are not (see `uncovered`). */
export function bandProblems(bands: readonly ClockBand[], rounds: number): string[] {
  const out: string[] = [];
  bands.forEach((b, i) => {
    if (b.fromRound < 1 || b.toRound > rounds) {
      out.push(`Band ${i + 1} runs outside rounds 1–${rounds}.`);
    }
    if (b.toRound < b.fromRound) out.push(`Band ${i + 1} ends before it starts.`);
    if (b.seconds <= 0) out.push(`Band ${i + 1} has no length.`);
    for (let j = i + 1; j < bands.length; j += 1) {
      const c = bands[j]!;
      if (b.fromRound <= c.toRound && c.fromRound <= b.toRound) {
        out.push(`Bands ${i + 1} and ${j + 1} both cover round ${Math.max(b.fromRound, c.fromRound)}.`);
      }
    }
  });
  return out;
}

/** Rounds with no clock, as ranges. */
export function uncovered(bands: readonly ClockBand[], rounds: number): string[] {
  const covered = new Set<number>();
  for (const b of bands) {
    for (let r = Math.max(1, b.fromRound); r <= Math.min(rounds, b.toRound); r += 1) covered.add(r);
  }
  const out: string[] = [];
  let start: number | null = null;
  for (let r = 1; r <= rounds + 1; r += 1) {
    if (r <= rounds && !covered.has(r)) {
      if (start === null) start = r;
    } else if (start !== null) {
      out.push(start === r - 1 ? `${start}` : `${start}–${r - 1}`);
      start = null;
    }
  }
  return out;
}

export interface Countdown {
  /** Seconds left, never negative. */
  left: number;
  /** True once the clock has run out. */
  over: boolean;
  /** `1:42`, or `0:07`. */
  text: string;
}

/**
 * A countdown from a start instant and a length. Null when either is unknown.
 *
 * `held` is time the room was stopped between `since` and `now`, and it is what makes a pause
 * actually pause rather than merely freeze the picture — see `domain/auctionTime.ts`. Zero for a
 * clock that has no pause ledger behind it, which is every clock outside the farm auction.
 */
export function countdown(
  since: string | null | undefined,
  seconds: number | null | undefined,
  now = Date.now(),
  held = 0,
): Countdown | null {
  if (!since || !seconds || seconds <= 0) return null;
  const at = Date.parse(since);
  if (Number.isNaN(at)) return null;
  const left = Math.max(0, Math.ceil((at + held + seconds * 1000 - now) / 1000));
  return {
    left,
    over: left === 0,
    text: `${Math.floor(left / 60)}:${String(left % 60).padStart(2, '0')}`,
  };
}

/**
 * Whether a standing farm bid has stood unchallenged long enough to sell. The only clock here that
 * decides anything. Every raise resets it, so two bidders trading raises are never cut off.
 */
export function bidHasSold(
  bidAt: string | null | undefined,
  clocks: DraftClocks | null | undefined,
  now = Date.now(),
  held = 0,
): boolean {
  /*
   * `held` MUST BE THE SAME FIGURE THE DISPLAYED CLOCK USED. A going-gone clock that subtracted the
   * pauses while the hammer did not would let the room watch a number reach zero with nothing
   * happening; the other way round sells a lot whose clock still reads 0:14.
   */
  const c = countdown(bidAt, clocks?.bidSellsAfterSeconds, now, held);
  return c ? c.over : false;
}

/** Is the scheduled intermission due after this round? */
export function intermissionAfter(
  clocks: DraftClocks | null | undefined,
  round: number,
): number | null {
  if (clocks?.intermissionAfterRound !== round) return null;
  return clocks?.intermissionMinutes && clocks.intermissionMinutes > 0
    ? clocks.intermissionMinutes
    : null;
}
